import axios from "axios";

// 添加響應攔截器
axios.interceptors.response.use(
  (response) => {
    // 檢查響應數據是否包含必要的字段
    if (response.config.responseType === 'blob') {
      return response;
    }
    if (!response.data) {
      console.error("響應數據為空：", response);
      throw new Error("響應數據為空");
    }

    // 如果響應中有Success字段，則檢查它
    if ("Success" in response.data && !response.data.Success) {
      console.error("響應失敗：", response.data);
      throw new Error(response.data.Message || "請求失敗");
    }

    return response;
  },
  (error) => {
    // 處理錯誤響應
    console.error("請求錯誤：", error);

    if (error.response) {
      // 服務器返回了錯誤狀態碼
      const status = error.response.status;
      const message =
        error.response.data?.Message ||
        error.response.data?.message ||
        "未知錯誤";
      console.error("服務器錯誤響應：", {
        status,
        message,
        data: error.response.data,
      });
      throw new Error(`請求失敗 (${status}): ${message}`);
    } else if (error.request) {
      // 請求已發出但沒有收到響應
      console.error("網絡錯誤：", error.request);
      throw new Error("網絡錯誤：無法連接到服務器");
    } else {
      // 請求配置出錯
      console.error("請求配置錯誤：", error.message);
      throw new Error(`請求配置錯誤: ${error.message}`);
    }
  }
);

// 容量分析相關API
export const capacityAnalysis = {
  // 獲取指定容量分析
  get: async (capacityAnalysisId, projectId) => {
    try {
      console.log("正在獲取容量分析，參數：", { capacityAnalysisId, projectId });
      
      const response = await axios.get("/api/RRCA/CapacityAnalysis/Get", {
        params: {
          CapacityAnalysisId: capacityAnalysisId,
          ProjectId: projectId,
        },
      });
      
      console.log("容量分析API回應：", response.data);
      
      // 檢查回應結構 - RRCA.CapacityAnalysisGetResponse 可能沒有 Success 欄位
      // 而是直接包含 CapacityAnalyses 陣列
      if (response.data) {
        // 如果有 Success 欄位，檢查它
        if (Object.prototype.hasOwnProperty.call(response.data, 'Success') && !response.data.Success) {
          throw new Error(response.data.Message || "獲取容量分析失敗");
        }
        // 如果沒有 Success 欄位，檢查是否有 CapacityAnalyses 資料
        if (!Object.prototype.hasOwnProperty.call(response.data, 'Success') && !response.data.CapacityAnalyses) {
          throw new Error("回應格式不正確，缺少 CapacityAnalyses 資料");
        }
        return response.data;
      } else {
        throw new Error("獲取容量分析失敗：無回應資料");
      }
    } catch (error) {
      console.error("獲取容量分析失敗:", error);
      // 如果是 axios 錯誤，提供更詳細的錯誤資訊
      if (error.response) {
        console.error("API 回應錯誤:", error.response.status, error.response.data);
        throw new Error(`API 錯誤 ${error.response.status}: ${error.response.data?.Message || error.response.statusText}`);
      } else if (error.request) {
        console.error("網路請求失敗:", error.request);
        throw new Error("網路請求失敗，請檢查網路連接");
      } else {
        throw error;
      }
    }
  },

  // 創建容量分析
  create: async (params) => {
    try {
      const response = await axios.post(
        "/api/RRCA/CapacityAnalysis/Create",
        params
      );
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建容量分析失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建容量分析失敗:", error);
      throw error;
    }
  },

  // 獲取容量分析進度
  getProgress: async (processId) => {
    try {
      console.log("正在獲取分析進度，processId:", processId);
      const response = await axios.get(
        "/api/RRCA/CapacityAnalysis/GetProgress",
        {
          params: {
            ProcessId: processId,
          },
        }
      );

      console.log("進度響應：", response);

      // 檢查響應數據
      if (!response.data?.Process) {
        console.warn("未獲取到進度數據");
        throw new Error("未獲取到進度數據");
      }

      return {
        Success: true,
        Process: response.data.Process,
      };
    } catch (error) {
      console.error("獲取容量分析進度失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取容量分析進度失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 獲取容量分析結果
  getResult: async (capacityAnalysisResultId) => {
    try {
      console.log(
        "正在獲取分析結果，capacityAnalysisResultId:",
        capacityAnalysisResultId
      );

      if (!capacityAnalysisResultId) {
        throw new Error("缺少必要參數：capacityAnalysisResultId");
      }

      const response = await axios.get("/api/RRCA/CapacityAnalysis/GetResult", {
        params: {
          CapacityAnalysisId: capacityAnalysisResultId,
        },
      });

      console.log("分析結果響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // 檢查響應數據結構
      if (!response.data.Results) {
        console.warn("未獲取到分析結果，返回空數組");
        response.data.Results = [];
      }

      return {
        Success: true,
        Results: response.data.Results,
      };
    } catch (error) {
      console.error("獲取容量分析結果失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取容量分析結果失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 獲取容量分析結果索引
  getResultIndex: async (projectId, limit, page) => {
    try {
      console.log("正在獲取容量分析結果索引，參數：", {
        projectId,
        limit,
        page,
      });

      if (!projectId) {
        throw new Error("缺少必要參數：projectId");
      }

      if (!limit || !page) {
        console.warn("未提供limit或page參數，使用默認值");
        limit = limit || 10;
        page = page || 1;
      }

      const response = await axios.get("/api/RRCA/CapacityAnalysis/Get", {
        params: {
          ProjectId: projectId,
        },
      });

      console.log("容量分析結果索引響應：", response);

      // 檢查響應數據
      if (!response.data?.CapacityAnalyses) {
        console.warn("未獲取到容量分析結果，返回空數組");
        return {
          Results: [],
          Total: 0,
          Success: true,
        };
      }

      // 將CapacityAnalyses轉換為所需的格式
      const results = response.data.CapacityAnalyses.map((analysis) => ({
        CapacityAnalysisId: analysis.CapacityAnalysisId,
        ProjectId: analysis.ProjectId,
        AnalysisTime: analysis.AnalysisTime,
        Status: analysis.Status || 3, // 默認為已完成
        OperationMarginFactor: analysis.OperationMarginFactor,
        SimulatedTrainNumber: analysis.SimulatedTrainNumber,
        RandomSeed: analysis.RandomSeed,
      }));

      // 處理分頁
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedResults = results.slice(startIndex, endIndex);

      return {
        Results: paginatedResults,
        Total: results.length,
        Success: true,
      };
    } catch (error) {
      console.error("獲取容量分析結果索引失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取容量分析結果索引失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },
};

// 車廂相關API
export const carriage = {
  // 獲取指定車廂
  get: async (trainId) => {
    try {
      console.log("正在獲取車廂數據，參數：", { trainId });

      const params = {};
      if (trainId) {
        params.TrainId = trainId;
      }

      const response = await axios.get("/api/RRCA/Carriage/Get", {
        params: params,
      });

      console.log("車廂數據響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // 檢查響應數據結構
      if (!response.data.Carriages) {
        console.warn("未獲取到車廂數據，返回空數組");
        response.data.Carriages = [];
      }

      // 添加Success字段以保持一致性
      response.data.Success = true;

      return response.data;
    } catch (error) {
      console.error("獲取車廂失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取車廂失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 創建車廂
  create: async (params) => {
    try {
      const response = await axios.post("/api/RRCA/Carriage/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建車廂失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建車廂失敗:", error);
      throw error;
    }
  },

  // 更新車廂
  update: async (params) => {
    try {
      const response = await axios.put("/api/RRCA/Carriage/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新車廂失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新車廂失敗:", error);
      throw error;
    }
  },

  // 刪除車廂
  delete: async (carriageId) => {
    try {
      const response = await axios.delete("/api/RRCA/Carriage/Delete", {
        data: {
          CarriageId: carriageId,
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除車廂失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除車廂失敗:", error);
      throw error;
    }
  },
};

// 自訂信號間隔相關API
export const customSignalInterval = {
  // 獲取指定自訂信號間隔
  get: async (params) => {
    try {
      console.log(
        "Sending GET request to /api/RRCA/CustomSignalInterval/Get with params:",
        params
      );

      const response = await axios.get("/api/RRCA/CustomSignalInterval/Get", {
        params: params, // Pass parameters in the query string
      });

      console.log("自訂信號間隔響應：", response);

      // The global interceptor ensures response.data exists.
      // If 'Success' was present in response.data and false, the interceptor would have thrown.
      // So, at this point, response.data.Success is either true or undefined.

      // For this specific endpoint: if 'Success' is not provided in the response,
      // but the main data payload (CustomSignalInterval) is present, it implies success.
      if (typeof response.data.Success === "undefined") {
        if (response.data.CustomSignalInterval) {
          // Data payload is present, so we mark it as a successful response.
          response.data.Success = true;
        } else {
          // 'Success' is undefined and the main data payload is also missing.
          const errorMessage =
            response.data.Message ||
            "獲取自訂信號間隔失敗: 必要數據缺失且無成功標誌";
          console.error(errorMessage, response.data);
          throw new Error(errorMessage);
        }
      }

      // If response.data.Success is now true, ensure the CustomSignalInterval object exists.
      // If response.data.Success was false from the API (and somehow bypassed interceptor for 'Success' field absence),
      // this block will handle it by throwing an error.
      if (response.data.Success === true) {
        if (!response.data.CustomSignalInterval) {
          console.warn(
            "API響應標記成功但 CustomSignalInterval 數據缺失，返回空物件。"
          );
          response.data.CustomSignalInterval = {}; // Provide default empty object
        }
      } else {
        // This means response.data.Success is false
        const errorMessage =
          response.data.Message ||
          "獲取自訂信號間隔失敗 (API 回應 Success=false)";
        console.error(errorMessage, response.data);
        throw new Error(errorMessage);
      }

      return response.data; // Return the entire response data object, now with a consistent Success flag
    } catch (error) {
      console.error("獲取自訂信號間隔失敗 (catch block):", error);
      // Error might have been formatted by the interceptor or thrown above.
      throw error;
    }
  },

  // 更新指定自訂信號間隔
  update: async (params) => {
    try {
      const response = await axios.put(
        "/api/RRCA/CustomSignalInterval/Update",
        params
      );
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新自訂信號間隔失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新自訂信號間隔失敗:", error);
      throw error;
    }
  },
};

// 欄位約束相關API
export const fieldConstraint = {
  // 獲取已經被設定的檢核設定欄位數值
  get: async (projectId, table, column) => {
    try {
      const response = await axios.get("/api/RRCA/FieldConstraint/Get", {
        params: {
          ProjectId: projectId,
          Table: table,
          Column: column,
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "獲取檢核設定欄位數值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("獲取檢核設定欄位數值失敗:", error);
      throw error;
    }
  },

  // 更新檢核設定欄位數值
  update: async (params) => {
    try {
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/Update",
        params
      );
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新檢核設定欄位數值失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定欄位數值失敗:", error);
      throw error;
    }
  },

  // 獲取檢核設定(全域參數)
  getGlobalSetting: async (projectId) => {
    try {
      console.log("正在獲取全域檢核設定...");
      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;
        
      const response = await axios.get(
        "/api/RRCA/FieldConstraint/GetGlobalSetting",
        {
          params: { ProjectId: cleanProjectId },
        }
      );
      console.log("全域檢核設定響應:", response);

      if (!response.data) {
        console.error("獲取全域檢核設定失敗: 響應數據為空");
        throw new Error("響應數據為空");
      }

      // Even if Success is false, the data might contain constraints info
      // Let the store handle the Success flag if needed, but return the data
      // Add Success field for consistency if missing (assuming true if no error)
      if (typeof response.data.Success === "undefined") {
        response.data.Success = true;
      }

      // The response should directly contain Default, Minimum, Maximum according to OpenAPI spec
      // But if the backend returns it wrapped in FieldConstraintGlobalSetting, unwrap it
      if (response.data.FieldConstraintGlobalSetting) {
        console.log("Unwrapping FieldConstraintGlobalSetting structure");
        const unwrapped = {
          Success: response.data.Success,
          Default: response.data.FieldConstraintGlobalSetting.Default,
          Minimum: response.data.FieldConstraintGlobalSetting.Minimum,
          Maximum: response.data.FieldConstraintGlobalSetting.Maximum,
        };
        return unwrapped;
      }

      // Check for expected direct structure
      if (!response.data.Default && !response.data.Minimum && !response.data.Maximum) {
        console.warn("未在響應中找到檢核約束結構，返回空約束");
        response.data = {
          Success: response.data.Success || true,
          Default: {},
          Minimum: {},
          Maximum: {}
        };
      }

      return response.data;
    } catch (error) {
      console.error("獲取檢核設定(全域參數)失敗:", error);
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取全域檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 獲取檢核設定(列車參數)
  getTrain: async (projectId) => {
    try {
      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;
        
      const response = await axios.get("/api/RRCA/FieldConstraint/GetTrain", {
        params: { ProjectId: cleanProjectId },
      });
      
      console.log("getTrain API 原始回應:", response.data);
      
      // 檢查是否有回應資料
      if (!response.data) {
        throw new Error("API 回應為空");
      }
      
      // 檢查 Success 字段，如果沒有則根據是否有資料來判斷
      const hasData = response.data.Default || response.data.Minimum || response.data.Maximum;
      const isSuccess = response.data.Success !== false && hasData;
      
      if (!isSuccess) {
        throw new Error(response.data?.Message || "獲取檢核設定(列車參數)失敗");
      }
      
      // 將後端的資料結構轉換為前端期望的格式
      const transformedData = {
        Success: true,
        FieldConstraintTrain: {}
      };
      
      // 轉換 Default 資料
      if (response.data.Default) {
        const defaultData = response.data.Default;
        transformedData.FieldConstraintTrain.DefaultTrainLength = defaultData.TrainLength;
        transformedData.FieldConstraintTrain.DefaultDrivingAcceleration = defaultData.DrivingAcceleration;
        transformedData.FieldConstraintTrain.DefaultDrivingDeceleration = defaultData.DrivingDeceleration;
        transformedData.FieldConstraintTrain.DefaultAccelerationEffectiveFactor = defaultData.AccelerationEffectiveFactor;
        transformedData.FieldConstraintTrain.DefaultDecelerationEffectiveFactor = defaultData.DecelerationEffectiveFactor;
      }
      
      // 轉換 Minimum 資料
      if (response.data.Minimum) {
        const minimumData = response.data.Minimum;
        transformedData.FieldConstraintTrain.MinTrainLength = minimumData.TrainLength;
        transformedData.FieldConstraintTrain.MinDrivingAcceleration = minimumData.DrivingAcceleration;
        transformedData.FieldConstraintTrain.MinDrivingDeceleration = minimumData.DrivingDeceleration;
        transformedData.FieldConstraintTrain.MinAccelerationEffectiveFactor = minimumData.AccelerationEffectiveFactor;
        transformedData.FieldConstraintTrain.MinDecelerationEffectiveFactor = minimumData.DecelerationEffectiveFactor;
      }
      
      // 轉換 Maximum 資料
      if (response.data.Maximum) {
        const maximumData = response.data.Maximum;
        transformedData.FieldConstraintTrain.MaxTrainLength = maximumData.TrainLength;
        transformedData.FieldConstraintTrain.MaxDrivingAcceleration = maximumData.DrivingAcceleration;
        transformedData.FieldConstraintTrain.MaxDrivingDeceleration = maximumData.DrivingDeceleration;
        transformedData.FieldConstraintTrain.MaxAccelerationEffectiveFactor = maximumData.AccelerationEffectiveFactor;
        transformedData.FieldConstraintTrain.MaxDecelerationEffectiveFactor = maximumData.DecelerationEffectiveFactor;
      }
      
      console.log("getTrain API 轉換後資料:", transformedData);
      
      return transformedData;
    } catch (error) {
      console.error("獲取檢核設定(列車參數)失敗:", error);
      // Handle errors similarly to getGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`獲取列車檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 獲取檢核設定(車站參數)
  getStation: async (projectId) => {
    try {
      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;
        
      const response = await axios.get("/api/RRCA/FieldConstraint/GetStation", {
        params: { ProjectId: cleanProjectId },
      });
      
      console.log("getStation API 原始回應:", response.data);
      console.log("原始回應中的 Default 資料:", response.data.Default);
      console.log("原始回應中的 Minimum 資料:", response.data.Minimum);
      console.log("原始回應中的 Maximum 資料:", response.data.Maximum);
      
      // 檢查是否有回應資料
      if (!response.data) {
        throw new Error("API 回應為空");
      }
      
      // 檢查 Success 字段，如果沒有則根據是否有資料來判斷
      const hasData = response.data.Default || response.data.Minimum || response.data.Maximum;
      const isSuccess = response.data.Success !== false && hasData;
      
      if (!isSuccess) {
        throw new Error(response.data?.Message || "獲取檢核設定(車站參數)失敗");
      }
      
      // 檢查是否已經是正確的格式（包含 FieldConstraintStation）
      if (response.data.FieldConstraintStation) {
        console.log("回應已包含 FieldConstraintStation 結構");
        return response.data;
      }
      
      // 如果是直接的 Default/Minimum/Maximum 結構，則轉換為期望的格式
      if (hasData) {
        // 由於車站參數的欄位結構可能不同，我們需要將 Default/Minimum/Maximum 轉換為平面結構
        const transformedData = {
          Success: true,
          FieldConstraintStation: {}
        };
        
        // 轉換 Default 資料
        if (response.data.Default) {
          const defaultData = response.data.Default;
          Object.keys(defaultData).forEach(key => {
            const transformedKey = `Default${key}`;
            transformedData.FieldConstraintStation[transformedKey] = defaultData[key];
          });
          
          // 正確對應 TurnoutToSignalDistance 欄位
          if (defaultData.TurnoutToSignalDistance !== undefined) {
            transformedData.FieldConstraintStation.DefaultTurnoutToSignalDistance = defaultData.TurnoutToSignalDistance;
          }
        }
        
        // 轉換 Minimum 資料
        if (response.data.Minimum) {
          const minimumData = response.data.Minimum;
          Object.keys(minimumData).forEach(key => {
            const transformedKey = `Min${key}`;
            transformedData.FieldConstraintStation[transformedKey] = minimumData[key];
          });
          
          // 正確對應 TurnoutToSignalDistance 欄位
          if (minimumData.TurnoutToSignalDistance !== undefined) {
            transformedData.FieldConstraintStation.MinTurnoutToSignalDistance = minimumData.TurnoutToSignalDistance;
          }
        }
        
        // 轉換 Maximum 資料
        if (response.data.Maximum) {
          const maximumData = response.data.Maximum;
          Object.keys(maximumData).forEach(key => {
            const transformedKey = `Max${key}`;
            transformedData.FieldConstraintStation[transformedKey] = maximumData[key];
          });
          
          // 正確對應 TurnoutToSignalDistance 欄位
          if (maximumData.TurnoutToSignalDistance !== undefined) {
            transformedData.FieldConstraintStation.MaxTurnoutToSignalDistance = maximumData.TurnoutToSignalDistance;
          }
        }
        
        // 特別檢查道岔到出發號誌距離的轉換結果
        console.log("轉換後的 FieldConstraintStation 完整資料:", transformedData.FieldConstraintStation);
        console.log('API資料轉換完成，道岔到出發號誌距離 (TurnoutToSignalDistance):', {
          Default: transformedData.FieldConstraintStation.DefaultTurnoutToSignalDistance,
          Min: transformedData.FieldConstraintStation.MinTurnoutToSignalDistance,
          Max: transformedData.FieldConstraintStation.MaxTurnoutToSignalDistance
        });
        
        // 檢查原始資料中是否有道岔相關的欄位
        if (response.data.Default) {
          console.log("Default 資料中的道岔相關欄位:", Object.keys(response.data.Default).filter(key => 
            key.toLowerCase().includes('turnout') || key.toLowerCase().includes('signal')
          ));
        }
        if (response.data.Minimum) {
          console.log("Minimum 資料中的道岔相關欄位:", Object.keys(response.data.Minimum).filter(key => 
            key.toLowerCase().includes('turnout') || key.toLowerCase().includes('signal')
          ));
        }
        if (response.data.Maximum) {
          console.log("Maximum 資料中的道岔相關欄位:", Object.keys(response.data.Maximum).filter(key => 
            key.toLowerCase().includes('turnout') || key.toLowerCase().includes('signal')
          ));
        }
        
        console.log("getStation API 轉換後資料:", transformedData);
        return transformedData;
      }
      
      // 如果沒有資料，返回包含預設值的結構
      console.log("API 回應中沒有檢核設定資料，使用預設值");
      return {
        Success: true,
        FieldConstraintStation: {
          // 車頭到出發號誌距離的預設值
          DefaultHeadToSignalDistance: 130,
          MinHeadToSignalDistance: 0,
          MaxHeadToSignalDistance: 1000,
          // 道岔到出發號誌距離的預設值 (暫時與車頭到出發號誌距離相同)
          DefaultTurnoutToSignalDistance: 130,
          MinTurnoutToSignalDistance: 0,
          MaxTurnoutToSignalDistance: 1000,
          // 其他欄位的預設值
          DefaultArrivalSlope: 0,
          MinArrivalSlope: -30,
          MaxArrivalSlope: 30,
          DefaultDepartureSlope: 0,
          MinDepartureSlope: -30,
          MaxDepartureSlope: 30,
          DefaultBlockSection2Length: 1560,
          MinBlockSection2Length: 0,
          MaxBlockSection2Length: 3000,
          DefaultBlockSection1Length: 1502,
          MinBlockSection1Length: 0,
          MaxBlockSection1Length: 3000,
          DefaultBlockSectionLength: 785,
          MinBlockSectionLength: 0,
          MaxBlockSectionLength: 3000,
          DefaultBlockSectionNLength: 1502,
          MinBlockSectionNLength: 0,
          MaxBlockSectionNLength: 3000,
          DefaultBlockSectionNMinus1Length: 1560,
          MinBlockSectionNMinus1Length: 0,
          MaxBlockSectionNMinus1Length: 3000
        }
      };
    } catch (error) {
      console.error("獲取檢核設定(車站參數)失敗:", error);
      // Handle errors similarly to getGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`獲取車站檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 獲取檢核設定(交互關係參數)
  getInteractiveRelationship: async (projectId) => {
    try {
      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;
        
      const response = await axios.get(
        "/api/RRCA/FieldConstraint/GetInteractiveRelationship",
        {
          params: { ProjectId: cleanProjectId },
        }
      );
      
      console.log("getInteractiveRelationship API 原始回應:", response.data);
      
      // 檢查是否有回應資料
      if (!response.data) {
        throw new Error("API 回應為空");
      }
      
      // 檢查 Success 字段，如果沒有則根據是否有資料來判斷
      const hasData = response.data.Default || response.data.Minimum || response.data.Maximum;
      const isSuccess = response.data.Success !== false && hasData;
      
      if (!isSuccess) {
        throw new Error(response.data?.Message || "獲取檢核設定(交互關係參數)失敗");
      }
      
      // 根據 OpenAPI 規格，API 只返回三個通用欄位：CruiseSpeed, OperationTime, StopTime
      // 直接返回 {Success: true, Default: {...}, Minimum: {...}, Maximum: {...}} 格式
      if (hasData) {
        console.log("getInteractiveRelationship API 返回資料（OpenAPI 規格）:", {
          Default: response.data.Default,
          Minimum: response.data.Minimum,
          Maximum: response.data.Maximum
        });
        
        return {
          Success: true,
          Default: response.data.Default || {},
          Minimum: response.data.Minimum || {},
          Maximum: response.data.Maximum || {}
        };
      }
      
      // 如果沒有資料，返回空結構
      console.warn("getInteractiveRelationship API 無有效資料");
      return {
        Success: true,
        Default: {},
        Minimum: {},
        Maximum: {}
      };
    } catch (error) {
      console.error("獲取檢核設定(交互關係參數)失敗:", error);
      // Handle errors similarly to getGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`獲取交互關係檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 獲取檢核設定(各項分析參數)
  getAnalysis: async (projectId) => {
    try {
      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;
        
      const response = await axios.get(
        "/api/RRCA/FieldConstraint/GetAnalysis",
        {
          params: { ProjectId: cleanProjectId },
        }
      );
      
      console.log("getAnalysis API 原始回應:", response.data);
      
      // 檢查是否有回應資料
      if (!response.data) {
        throw new Error("API 回應為空");
      }
      
      // 檢查 Success 字段，如果沒有則根據是否有資料來判斷
      const hasData = response.data.Default || response.data.Minimum || response.data.Maximum;
      const isSuccess = response.data.Success !== false && hasData;
      
      if (!isSuccess) {
        throw new Error(response.data?.Message || "獲取檢核設定(各項分析參數)失敗");
      }
      
      // 根據 OpenAPI 規格，直接返回 {Default: {...}, Minimum: {...}, Maximum: {...}} 格式
      // 與 getGlobalSetting 和 getInteractiveRelationship 保持一致
      if (hasData) {
        console.log("getAnalysis API 返回資料（OpenAPI 規格）:", {
          Default: response.data.Default,
          Minimum: response.data.Minimum,
          Maximum: response.data.Maximum
        });
        
        return {
          Success: true,
          Default: response.data.Default || {},
          Minimum: response.data.Minimum || {},
          Maximum: response.data.Maximum || {}
        };
      }
      
      // 如果沒有資料，返回空結構
      console.warn("getAnalysis API 無有效資料");
      return {
        Success: true,
        Default: {},
        Minimum: {},
        Maximum: {}
      };
    } catch (error) {
      console.error("獲取檢核設定(各項分析參數)失敗:", error);
      // Handle errors similarly to getGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`獲取分析檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新檢核設定(全域參數)
  updateGlobalSetting: async (projectId, payload) => {
    try {
      console.log("正在更新全域檢核設定，projectId:", projectId, "payload:", payload);
      // Add projectId to payload
      const requestPayload = {
        ProjectId: projectId,
        ...payload
      };
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/UpdateGlobalSetting",
        requestPayload
      );
      console.log("更新全域檢核設定響應:", response);

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新檢核設定(全域參數)失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定(全域參數)失敗:", error);
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`更新全域檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新檢核設定(列車參數)
  updateTrain: async (projectId, params) => {
    try {
      console.log("正在更新列車檢核設定，projectId:", projectId, "params:", params);
      
      // 根據 OpenAPI 規範，請求格式應該包含 ProjectId, Default, Minimum, Maximum
      const requestPayload = {
        ProjectId: projectId,
        Default: params.Default || {},
        Minimum: params.Minimum || {},
        Maximum: params.Maximum || {}
      };
      
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/UpdateTrain",
        requestPayload
      );
      
      console.log("更新列車檢核設定響應:", response);
      
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新檢核設定(列車參數)失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定(列車參數)失敗:", error);
      // Handle errors similarly to updateGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`更新列車檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新檢核設定(車站參數)
  updateStation: async (projectId, params) => {
    try {
      console.log("正在更新車站檢核設定，projectId:", projectId, "params:", params);
      
      // 根據 OpenAPI 規範，請求格式應該包含 ProjectId, Default, Minimum, Maximum
      const requestPayload = {
        ProjectId: projectId,
        Default: params.Default || {},
        Minimum: params.Minimum || {},
        Maximum: params.Maximum || {}
      };
      
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/UpdateStation",
        requestPayload
      );
      
      console.log("更新車站檢核設定響應:", response);
      
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新檢核設定(車站參數)失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定(車站參數)失敗:", error);
      // Handle errors similarly to updateGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`更新車站檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新檢核設定(交互關係參數)
  updateInteractiveRelationship: async (projectId, payload) => {
    try {
      console.log("正在更新交互關係檢核設定，projectId:", projectId, "payload:", payload);
      
      // 根據 OpenAPI 規範，請求格式應該包含 ProjectId, Default, Minimum, Maximum
      const requestPayload = {
        ProjectId: projectId,
        Default: payload.Default || {},
        Minimum: payload.Minimum || {},
        Maximum: payload.Maximum || {}
      };
      
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/UpdateInteractiveRelationship",
        requestPayload
      );
      
      console.log("更新交互關係檢核設定響應:", response);
      
      if (!response.data?.Success) {
        throw new Error(
          response.data?.Message || "更新檢核設定(交互關係參數)失敗"
        );
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定(交互關係參數)失敗:", error);
      // Handle errors similarly to updateGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`更新交互關係檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新檢核設定(各項分析參數)
  updateAnalysis: async (projectId, params) => {
    try {
      console.log("正在更新分析檢核設定，projectId:", projectId, "params:", params);
      
      // 根據 OpenAPI 規範，請求格式應該包含 ProjectId, Default, Minimum, Maximum
      const requestPayload = {
        ProjectId: projectId,
        Default: params.Default || {},
        Minimum: params.Minimum || {},
        Maximum: params.Maximum || {}
      };
      
      const response = await axios.put(
        "/api/RRCA/FieldConstraint/UpdateAnalysis",
        requestPayload
      );
      
      console.log("更新分析檢核設定響應:", response);
      
      if (!response.data?.Success) {
        throw new Error(
          response.data?.Message || "更新檢核設定(各項分析參數)失敗"
        );
      }
      return response.data;
    } catch (error) {
      console.error("更新檢核設定(各項分析參數)失敗:", error);
      // Handle errors similarly to updateGlobalSetting
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        throw new Error(`更新分析檢核設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },
};

// 全域設定相關API
export const globalSetting = {
  // 獲取指定專案的全域設定
  get: async (projectId) => {
    try {
      console.log("正在獲取全域設定，參數：", { projectId });

      if (!projectId) {
        throw new Error("缺少必要參數：projectId");
      }

      // 確保 projectId 是純粹的數值或字串，避免物件嵌套
      const cleanProjectId = typeof projectId === 'object' && projectId.ProjectId 
        ? projectId.ProjectId 
        : projectId;

      console.log("清理後的 projectId：", cleanProjectId);

      const response = await axios.get("/api/RRCA/GlobalSetting/Get", {
        params: {
          ProjectId: cleanProjectId,
        },
      });

      console.log("全域設定響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // Add Success flag for consistency, assume true if no error
      if (typeof response.data.Success === "undefined") {
        response.data.Success = true;
      }

      // 檢查響應數據結構 - Store now handles default structure creation
      if (!response.data.GlobalSetting) {
        console.warn("API未返回 GlobalSetting 物件，返回空物件");
        response.data.GlobalSetting = {}; // Return empty, let store merge defaults
        // response.data.GlobalSetting = { // Old default logic moved to store
        //   InterTrainLoadFactor: 0,
        //   SecondTrackProbability: 0,
        //   ThirdTrackProbability: 0,
        //   CustomSignalInterval: { /* ... defaults ... */ },
        //   UnblockSignalTime: 0,
        //   RoadOperationTime: 0,
        //   SingleTrackSignalTime: 0,
        //   AttentionSignalLimit: 0,
        //   EngineerReactionTime: 0
        // };
      }

      return response.data; // Return the whole data object including Success flag and GlobalSetting
    } catch (error) {
      console.error("獲取全域設定失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取全域設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        // 請求配置出錯
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },

  // 更新指定專案的全域設定
  update: async (payload) => {
    // payload should be constructed by the store action
    try {
      console.log("正在更新全域設定，payload:", payload);
      const response = await axios.put(
        "/api/RRCA/GlobalSetting/Update",
        payload
      );
      console.log("更新全域設定響應:", response);

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新全域設定失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新全域設定失敗:", error);
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`更新全域設定失敗 (${status}): ${message}`);
      } else if (error.request) {
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      } else {
        throw new Error(`請求配置錯誤: ${error.message}`);
      }
    }
  },
};

// 交互關係相關API
export const interactiveRelationship = {
  // 獲取指定交互關係
  get: async (
    interactiveRelationshipId,
    projectId,
    startStationId,
    endStationId
  ) => {
    try {
      console.log("正在獲取交互關係數據，參數：", {
        interactiveRelationshipId,
        projectId,
        startStationId,
        endStationId,
      });

      if (!projectId) {
        throw new Error("缺少必要參數：projectId");
      }

      const params = {
        ProjectId: projectId,
      };

      if (interactiveRelationshipId) {
        params.InteractiveRelationshipId = interactiveRelationshipId;
      }
      if (startStationId) {
        params.StartStationId = startStationId;
      }
      if (endStationId) {
        params.EndStationId = endStationId;
      }

      const response = await axios.get(
        "/api/RRCA/InteractiveRelationship/Get",
        {
          params: params,
        }
      );

      console.log("交互關係數據響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // 檢查響應數據結構
      if (!response.data.InteractiveRelationships) {
        console.warn("未獲取到交互關係數據，返回空數組");
        response.data.InteractiveRelationships = [];
      }

      // 添加Success字段以保持一致性
      response.data.Success = true;

      return response.data;
    } catch (error) {
      console.error("獲取交互關係失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取交互關係失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 更新指定交互關係
  update: async (params) => {
    try {
      const response = await axios.put(
        "/api/RRCA/InteractiveRelationship/Update",
        params
      );
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新交互關係失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新交互關係失敗:", error);
      throw error;
    }
  },
};

// 專案相關API
export const project = {
  // 匯出專案
  export: async (projectId, projectName) => {
    try {
      const response = await axios.post('/api/RRCA/Project/Export', { ProjectId: projectId });
      const fileContent = response.data?.JsonData || response.data?.File;

      if (fileContent) {
        const blob = new Blob([fileContent], { type: 'application/json;charset=utf-8' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        
        // Extract base name from server response or use provided projectName
        // Ensure the file extension is always .rrca (not .json)
        let baseName = projectName || 'project';
        if (response.data.FileName) {
          // Remove any existing extension (.json, .rrca, etc.) from server-provided filename
          baseName = response.data.FileName.replace(/\.(json|rrca)$/i, '');
        }
        // Ensure baseName doesn't already have an extension
        baseName = baseName.replace(/\.(json|rrca)$/i, '');
        const fileName = `${baseName}.rrca`;
        
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } else {
        console.error('無效的伺服器回應:', response.data);
        throw new Error(response.data.Message || '匯出專案失敗：無效的伺服器回應');
      }
    } catch (error) {
      console.error('匯出專案失敗:', error);
      throw error;
    }
  },

  // 匯入專案
  import: async (file) => {
    try {
      const jsonData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsText(file);
      });

      const response = await axios.post('/api/RRCA/Project/Import', {
        JsonData: jsonData
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.data?.Success) {
        throw new Error(response.data?.Message || '匯入專案失敗');
      }
      return response.data;
    } catch (error) {
      console.error('匯入專案失敗:', error);
      throw error;
    }
  },

  // 獲取指定專案
  get: async (projectId) => {
    try {
      console.log("正在獲取專案資料，參數：", { projectId });
      
      const response = await axios.get("/api/RRCA/Project/Get", {
        params: {
          ProjectId: projectId,
        },
      });
      
      console.log("專案資料響應：", response);
      
      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }
      
      // 添加 Success 欄位以保持一致性
      if (typeof response.data.Success === "undefined") {
        // 如果有 Project 資料，視為成功
        if (response.data.Project || response.data.Projects) {
          response.data.Success = true;
        } else {
          response.data.Success = false;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error("獲取專案失敗:", error);
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取專案失敗 (${status}): ${message}`);
      } else if (error.request) {
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      throw error;
    }
  },

  // 獲取當前帳號所有專案
  index: async (limit, page) => {
    try {
      console.log("正在獲取專案列表，參數：", { limit, page });

      if (!limit || !page) {
        throw new Error("缺少必要參數：limit 和 page");
      }

      const response = await axios.get("/api/RRCA/Project/Index", {
        params: {
          Limit: limit,
          Page: page,
        },
      });

      console.log("專案列表響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      console.log("響應數據：", response.data);

      // 檢查響應數據結構
      if (!response.data.Projects) {
        console.warn("未獲取到專案列表數據，返回空數組");
        response.data.Projects = [];
      }

      // 添加Success字段以保持一致性
      response.data.Success = true;

      return response.data;
    } catch (error) {
      console.error("獲取專案列表時發生錯誤：", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取專案列表失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 建立專案
  create: async (formData) => {
    try {
      const response = await axios.post("/api/RRCA/Project/Create", {
        ProjectName: formData.ProjectName,
        EditMode: formData.EditMode,
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "建立專案失敗");
      }
      return response.data;
    } catch (error) {
      console.error("建立專案失敗:", error);
      throw error;
    }
  },

  // 更新專案
  update: async (project) => {
    const response = await axios.put("/api/RRCA/Project/Update", project);
    return response.data;
  },

  // 取得範本
  getTemplate: async () => {
    const response = await axios.get('/api/RRCA/Project/GetTemplate');
    return response.data;
  },

  // 建立範本
  createTemplate: async (params) => {
    const response = await axios.post('/api/RRCA/Project/CreateTemplate', params);
    return response.data;
  },

  // 使用範本建立專案
  cloneTemplate: async (params) => {
    const response = await axios.post('/api/RRCA/Project/CloneTemplate', params);
    return response.data;
  },

  // 刪除專案
  delete: async (projectId) => {
    try {
      const response = await axios.delete("/api/RRCA/Project/Delete", {
        data: { ProjectId: projectId },
      });
      if (response.status === 204) {
        return { Success: true, Message: "專案已成功刪除 (204 No Content)" };
      }
      if (response.data && typeof response.data.Success !== "undefined") {
        return response.data;
      }
      if (response.status >= 200 && response.status < 300) {
        return {
          Success: true,
          Message: response.data?.Message || "專案已成功刪除",
        };
      }
      return {
        Success: false,
        Message: response.data?.Message || "刪除專案時發生未知錯誤",
      };
    } catch (error) {
      console.error("刪除專案 API 呼叫失敗:", error);
      const message =
        error.response?.data?.Message ||
        error.message ||
        "刪除專案時發生網路或伺服器錯誤";
      return { Success: false, Message: message };
    }
  },

  // 複製專案
  clone: async (sourceProjectId, newProjectName) => {
    try {
      const response = await axios.post("/api/RRCA/Project/Clone", {
        ProjectId: sourceProjectId,
        ProjectName: newProjectName,
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "複製專案失敗");
      }
      return response.data;
    } catch (error) {
      console.error("複製專案失敗:", error);
      throw error;
    }
  },
};

// 車站相關API
export const station = {
  // 獲取指定車站
  get: async (stationId, projectId) => {
    try {
      console.log("正在獲取車站數據，參數：", { stationId, projectId });

      if (!projectId) {
        throw new Error("缺少必要參數：projectId");
      }

      const params = {
        ProjectId: projectId,
      };

      if (stationId) {
        params.StationId = stationId;
      }

      const response = await axios.get("/api/RRCA/Station/Get", {
        params: params,
      });

      console.log("車站數據響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // 檢查響應數據結構
      if (!response.data.Stations) {
        console.warn("未獲取到車站數據，返回空數組");
        response.data.Stations = [];
      }

      // 添加Success字段以保持一致性
      response.data.Success = true;

      return response.data;
    } catch (error) {
      console.error("獲取車站失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取車站失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 創建車站
  create: async (params) => {
    try {
      const response = await axios.post("/api/RRCA/Station/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建車站失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建車站失敗:", error);
      throw error;
    }
  },

  // 更新車站
  update: async (params) => {
    try {
      const response = await axios.put("/api/RRCA/Station/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新車站失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新車站失敗:", error);
      throw error;
    }
  },

  // 刪除車站
  delete: async (stationId) => {
    try {
      const response = await axios.delete("/api/RRCA/Station/Delete", {
        data: {
          StationId: stationId,
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除車站失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除車站失敗:", error);
      throw error;
    }
  },

  // 重新排序車站
  reorder: async (params) => {
    try {
      const response = await axios.post("/api/RRCA/Station/Reorder", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "重新排序車站失敗");
      }
      return response.data;
    } catch (error) {
      console.error("重新排序車站失敗:", error);
      throw error;
    }
  },
};

// 列車相關API
export const train = {
  // 獲取指定列車
  get: async (trainId, projectId) => {
    try {
      console.log("正在獲取列車數據，參數：", { trainId, projectId });

      if (!projectId) {
        throw new Error("缺少必要參數：projectId");
      }

      const params = {
        ProjectId: projectId,
      };

      if (trainId) {
        params.TrainId = trainId;
      }

      const response = await axios.get("/api/RRCA/Train/Get", {
        params: params,
      });

      console.log("列車數據響應：", response);

      // 檢查響應數據
      if (!response.data) {
        console.error("響應數據為空");
        throw new Error("響應數據為空");
      }

      // 檢查響應數據結構
      if (!response.data.Trains) {
        console.warn("未獲取到列車數據，返回空數組");
        response.data.Trains = [];
      }

      // 添加Success字段以保持一致性
      response.data.Success = true;

      return response.data;
    } catch (error) {
      console.error("獲取列車失敗:", error);
      if (error.response) {
        // 服務器返回錯誤狀態碼
        const status = error.response.status;
        const message =
          error.response.data?.Message ||
          error.response.data?.message ||
          "未知錯誤";
        console.error("服務器錯誤響應：", {
          status,
          message,
          data: error.response.data,
        });
        throw new Error(`獲取列車失敗 (${status}): ${message}`);
      } else if (error.request) {
        // 請求已發出但沒有收到響應
        console.error("網絡錯誤：", error.request);
        throw new Error("網絡錯誤：無法連接到服務器");
      }
      // 其他錯誤直接拋出
      throw error;
    }
  },

  // 創建列車
  create: async (params) => {
    try {
      const response = await axios.post("/api/RRCA/Train/Create", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "創建列車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("創建列車失敗:", error);
      throw error;
    }
  },

  // 更新列車
  update: async (params) => {
    try {
      const response = await axios.put("/api/RRCA/Train/Update", params);
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "更新列車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("更新列車失敗:", error);
      throw error;
    }
  },

  // 刪除列車
  delete: async (trainId) => {
    try {
      const response = await axios.delete("/api/RRCA/Train/Delete", {
        data: {
          TrainId: trainId,
        },
      });
      if (!response.data?.Success) {
        throw new Error(response.data?.Message || "刪除列車失敗");
      }
      return response.data;
    } catch (error) {
      console.error("刪除列車失敗:", error);
      throw error;
    }
  },
};
